import { useEffect } from "react";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import type { CountryConfig } from "@orvis/core";
import { maybeShowAppOpenAd } from "../ads.js";

/**
 * A resume only counts as an "app open" after this long in the background —
 * not a glance at the notification shade, a permission dialog, or coming
 * back from tapping an ad (docs/ads-policy.md).
 */
const MIN_BACKGROUND_MS = 30_000;

/**
 * App Open ad (see ads.ts) on cold start and again whenever the app comes
 * back to the foreground.
 *
 * No-op on non-native platforms, mirrors `useBackButton`'s
 * `Capacitor.isNativePlatform()` guard and `@capacitor/app` listener pattern.
 */
export function useAppOpenAd(country: CountryConfig): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Cold start — there's no prior "resume" event to catch this with.
    void maybeShowAppOpenAd(country, { coldStart: true });

    let backgroundedAt = 0;
    const promise = App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        backgroundedAt = Date.now();
        return;
      }
      if (backgroundedAt && Date.now() - backgroundedAt >= MIN_BACKGROUND_MS) {
        void maybeShowAppOpenAd(country, { coldStart: false });
      }
      backgroundedAt = 0;
    });

    return () => {
      // Capacitor 6's addListener is thenable — clean up when it lands.
      void promise.then((handle) => handle.remove());
    };
  }, [country]);
}
